import { useId, useState } from 'react';
import { TertiaryButton } from '@ardoq/button';
import { pluralize } from '@ardoq/common-helpers';
import { colors, space } from '@ardoq/design-tokens';
import {
  DropdownContainer,
  DropdownFilterInput,
  DropdownMaxHeight,
  DropdownMenu,
  DropdownOption,
} from '@ardoq/dropdown-2026';
import {
  Checkbox,
  getChildElementId,
  NumberInput,
  TextInput,
} from '@ardoq/forms';
import { Icon, IconSize, MinimalIconProps, SpinnerIcon } from '@ardoq/icons';
import { FlexBox, Stack } from '@ardoq/layout';
import { header5, Text } from '@ardoq/typography';
import styled from 'styled-components';
import { Locale } from '@ardoq/locale';
import { type I18nLocale } from '@ardoq/i18n';
import {
  DateTimePickerDropdown,
  RelativeDatePicker,
  RelativeDatePickerMode,
} from '@ardoq/date-time-picker';
import { formatDateISO, parseLocaleDateString } from '@ardoq/date-time';
import { DropdownFootnote } from './DropdownFootnote';
import {
  HierarchicalListPopover,
  HierarchicalListPopoverProps,
} from './HierarchicalListPopover';
import { Separator } from './Separator';

type PopoverContentProps =
  | BooleanPopoverProps
  | DatePopoverProps
  | DateRangePopoverProps
  | NumberRangePopoverProps
  | RelativeDatePopoverProps
  | ListPopoverProps
  | HierarchicalListPopoverProps
  | StringPopoverProps
  | NumberPopoverProps;

export const PopoverContent = (props: PopoverContentProps) => {
  const popoverType = props.type;
  switch (popoverType) {
    case 'boolean':
      // Boolean filters are handled directly via onClick at the parent level
      return null;
    case 'list':
      return <ListPopover {...props} />;
    case 'hierarchicalList':
      return <HierarchicalListPopover {...props} />;
    case 'string':
      return <StringPopover {...props} />;
    case 'number':
      return <NumberPopover {...props} />;
    case 'date':
      return <DatePopover {...props} />;
    case 'relativeDate':
      return <RelativeDatePopover {...props} />;
    case 'numberRange':
      return <NumberRangePopover {...props} />;
    case 'dateRange':
      return <DateRangePopover {...props} />;
    default:
      popoverType satisfies never;
      throw new Error('Unsupported popover type');
  }
};

export type ListPopoverOptionProps = {
  id: string;
  label: string;
  isChecked: boolean;
  onClick: VoidFunction;
  icon: MinimalIconProps | null;
  iconImageUrl?: string;
  rightIcon: MinimalIconProps | null;
  count: number | null;
  onCheckboxClick: VoidFunction;
  breadCrumbs?: Array<{ text: string; icon?: MinimalIconProps }>;
  hideCheckbox?: boolean;
  isDisabled?: boolean;
  tooltip?: string;
  // Singular/plural function for the row's count label, defaults to English
  // "1 item" / "N items".
  itemCountLabel?: (count: number) => string;
};

export type ListPopoverGroupProps = {
  label?: string;
  options: ListPopoverOptionProps[];
};

export type ListPopoverProps = {
  type: 'list';
  maxHeight?: DropdownMaxHeight;
  filter?: {
    isLoading?: boolean; // For async suggestions
    ariaLabel?: string;
    placeholder?: string;
    value: string;
    onChange: (value: string) => void;
  }; // Optional for now
  groups: ListPopoverGroupProps[];
  loadingLabel?: string;
  noResultsLabel?: string;
  footer?: string;
};

const OptionsList = ({ options }: { options: ListPopoverOptionProps[] }) => {
  const checkedOptions = options.filter(option => option.isChecked);
  const uncheckedOptions = options.filter(option => !option.isChecked);
  return (
    <Stack>
      {checkedOptions.map(option => (
        <ListPopoverOption key={option.id} {...option} />
      ))}
      {checkedOptions.length > 0 && uncheckedOptions.length > 0 && (
        <Separator />
      )}
      {uncheckedOptions.map(option => (
        <ListPopoverOption key={option.id} {...option} />
      ))}
    </Stack>
  );
};

const GroupTitle = styled.h5`
  ${header5};
  padding: ${space.small} ${space.medium};
  margin: 0;
  color: ${colors.textSubtle};
`;

const hasOptions = (props: ListPopoverProps): boolean =>
  props.groups.some(group => group.options.length > 0);

const defaultItemCountLabel = (count: number): string =>
  `${count} ${pluralize('item', count)}`;

export const ListPopover = (props: ListPopoverProps) => {
  const isEmptyList = !hasOptions(props);

  return (
    <DropdownContainer dropdownMaxHeight={props.maxHeight}>
      <DropdownMenu>
        <Stack>
          {props.filter && (
            // Stops clicks from closing the popover. `display: contents`
            // preserves the input's `position: sticky` inside the scroll
            // container.
            <div
              onClick={e => e.stopPropagation()}
              style={{ display: 'contents' }}
            >
              <DropdownFilterInput
                aria-label={props.filter.ariaLabel}
                placeholder={props.filter.placeholder ?? 'Filter...'}
                value={props.filter.value}
                onValueChange={props.filter.onChange}
              />
            </div>
          )}
          {props.filter?.isLoading ? (
            <FlexBox
              padding="small"
              justify="center"
              align="center"
              gap="small"
            >
              <SpinnerIcon color={colors.icon} iconSize={IconSize.SMALL} />
              {props.loadingLabel ?? 'Loading...'}
            </FlexBox>
          ) : isEmptyList ? (
            <FlexBox padding="small" justify="center" align="center">
              <Text variant="text2" color="textSubtle">
                {props.noResultsLabel ?? 'No results'}
              </Text>
            </FlexBox>
          ) : (
            props.groups
              .filter(group => group.options.length)
              .map((group, i, groups) => (
                <div key={group.label || `group-${i}`}>
                  {group.label && <GroupTitle>{group.label}</GroupTitle>}
                  <OptionsList options={group.options} />
                  {i < groups.length - 1 && <Separator />}
                </div>
              ))
          )}
          {props.footer && (
            <>
              <Separator />
              <DropdownFootnote text={props.footer} />
            </>
          )}
        </Stack>
      </DropdownMenu>
    </DropdownContainer>
  );
};

export const ListPopoverOption = (props: ListPopoverOptionProps) => {
  const option = (
    <DropdownOption
      data-tooltip-text={props.tooltip}
      aria-label={props.label}
      isDisabled={props.isDisabled}
      onClick={props.onClick}
      onKeyDown={e => {
        if (e.key === ' ') {
          e.preventDefault();
          props.onCheckboxClick();
        }
      }}
    >
      <Stack gap="xsmall">
        <FlexBox align="center" minWidth="270px" maxWidth="270px">
          {!props.hideCheckbox && (
            <Checkbox
              name={props.label}
              aria-label={props.label}
              isChecked={props.isChecked}
              isDisabled={props.isDisabled}
              onChange={props.onCheckboxClick}
              tabIndex={-1}
              stopPropagation={true}
            />
          )}
          <FlexBox gap="xsmall" flex={1} align="center">
            {props.iconImageUrl ? (
              <img
                src={props.iconImageUrl}
                alt=""
                aria-hidden={true}
                style={{
                  width: IconSize.MEDIUM,
                  height: IconSize.MEDIUM,
                  objectFit: 'contain',
                }}
              />
            ) : (
              props.icon && (
                <Icon
                  iconName={props.icon.name}
                  color={props.icon.color ?? colors.icon}
                  iconSize={IconSize.MEDIUM}
                />
              )
            )}
            {props.label}
          </FlexBox>
          {props.rightIcon && (
            <Icon
              iconName={props.rightIcon.name}
              color={props.rightIcon.color ?? colors.icon}
              iconSize={IconSize.SMALL}
            />
          )}
          {props.count !== null && (
            <FlexBox
              marginLeft="auto"
              paddingLeft="small"
              style={{ color: colors.textSubtle }}
              minWidth="fit-content"
            >
              {(props.itemCountLabel ?? defaultItemCountLabel)(props.count)}
            </FlexBox>
          )}
        </FlexBox>
        {props.breadCrumbs && (
          <FlexBox paddingLeft="xlarge">
            {props.breadCrumbs.map(({ icon, text }, index) => (
              <FlexBox key={index} gap="small">
                {icon && (
                  <Icon
                    iconName={icon.name}
                    color={icon.color ?? colors.icon}
                    iconSize={IconSize.SMALL}
                  />
                )}
                <Text variant="caption" color="textSubtle">
                  {text}
                </Text>
              </FlexBox>
            ))}
          </FlexBox>
        )}
      </Stack>
    </DropdownOption>
  );

  return option;
};

export type StringPopoverProps = {
  type: 'string';
  value: string;
  label: string;
  isDisabled: boolean;
  setValue: (value: string) => void;
  onFormSubmit: VoidFunction;
  submitLabel?: string;
};

const StringPopover = (props: StringPopoverProps) => (
  <DropdownContainer>
    <form
      onSubmit={e => {
        e.preventDefault();
        props.onFormSubmit();
      }}
    >
      <FlexBox padding="small" gap="small" align="end">
        <TextInput
          label={props.label}
          value={props.value}
          onValueChange={props.setValue}
        />
        <TertiaryButton type="submit" isDisabled={props.isDisabled}>
          {props.submitLabel ?? 'Add filter'}
        </TertiaryButton>
      </FlexBox>
    </form>
  </DropdownContainer>
);

export type NumberPopoverProps = {
  type: 'number';
  value: number | null;
  label: string;
  isDisabled: boolean;
  allowDecimals: boolean;
  setValue: (value: number) => void;
  onFormSubmit: VoidFunction;
  errorMessage?: string;
  submitLabel?: string;
};

const NumberPopover = (props: NumberPopoverProps) => (
  <DropdownContainer>
    <form
      onSubmit={e => {
        e.preventDefault();
        props.onFormSubmit();
      }}
    >
      <FlexBox
        padding="small"
        gap="small"
        align={props.errorMessage ? 'center' : 'end'}
        minWidth="300px"
      >
        <NumberInput
          data-autofocus
          label={props.label}
          value={props.value ?? undefined}
          allowDecimals={props.allowDecimals}
          showUpDownArrows={false}
          onValueChange={props.setValue}
          errorMessage={props.errorMessage}
        />
        <TertiaryButton
          isFlexible={false}
          type="submit"
          isDisabled={props.isDisabled || Boolean(props.errorMessage)}
        >
          {props.submitLabel ?? 'Apply'}
        </TertiaryButton>
      </FlexBox>
    </form>
  </DropdownContainer>
);

export type DatePopoverProps = {
  type: 'date';
  value: string | null;
  label: string;
  isDisabled: boolean;
  setValue: (value: string | null) => void;
  locale: Locale;
  errorMessage?: string;
};

const DatePopover = (props: DatePopoverProps) => (
  <DropdownContainer dropdownMaxHeight={DropdownMaxHeight.AUTO}>
    <form
      onSubmit={e => {
        e.preventDefault();
      }}
    >
      <FlexBox
        padding="none"
        gap="small"
        align={props.errorMessage ? 'center' : 'end'}
        minWidth="300px"
      >
        <DateTimePickerDropdown
          data-autofocus
          config={{ locale: props.locale }}
          date={
            props.value
              ? parseLocaleDateString(props.value, props.locale)
              : null
          }
          onChange={date => {
            props.setValue(formatDateISO(date) ?? null);
          }}
        />
      </FlexBox>
    </form>
  </DropdownContainer>
);

export type BooleanPopoverProps = {
  type: 'boolean';
  onClick: VoidFunction;
};

/**
 * A date the user may express relative to today — `now-30d` as well as a
 * calendar date. The report filter sidebar has always accepted both, so a
 * filter moved onto the bar keeps that rather than losing it.
 */
export type RelativeDatePopoverProps = {
  type: 'relativeDate';
  value: string | undefined;
  label: string;
  placeholder: string;
  i18nLocale: I18nLocale;
  mode: RelativeDatePickerMode;
  isDisabled?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  setValue: (value: string | undefined) => void;
};

const RelativeDatePopover = (props: RelativeDatePopoverProps) => (
  <DropdownContainer dropdownMaxHeight={DropdownMaxHeight.AUTO}>
    <FlexBox padding="small" minWidth="300px">
      <RelativeDatePicker
        label={props.label}
        value={props.value}
        placeholder={props.placeholder}
        i18nLocale={props.i18nLocale}
        onChange={props.setValue}
        relativeDatePickerMode={props.mode}
        isDisabled={props.isDisabled}
        hasError={props.hasError}
        errorMessage={props.errorMessage}
      />
    </FlexBox>
  </DropdownContainer>
);

export type RangeBoundLabels = {
  fromPrefix: string;
  toPrefix: string;
  placeholder: string;
  startHigherError: string;
};

export type NumberRangeBounds = { start: number; end: number };

export type NumberRangePopoverProps = {
  type: 'numberRange';
  start: number | null;
  end: number | null;
  isDisabled?: boolean;
  labels: RangeBoundLabels;
  submitLabel: string;
  onSubmit: (bounds: NumberRangeBounds) => void;
};

type DraftBounds = { start: number | null; end: number | null };

const toBound = (value: number | undefined): number | null =>
  value === undefined || !isFinite(value) ? null : value;

const toValidBounds = ({
  start,
  end,
}: DraftBounds): NumberRangeBounds | null =>
  start !== null && end !== null && start <= end ? { start, end } : null;

export const NumberRangePopover = ({
  start,
  end,
  isDisabled,
  labels,
  submitLabel,
  onSubmit,
}: NumberRangePopoverProps) => {
  const [draft, setDraft] = useState<DraftBounds>({ start, end });
  const endInputId = useId();
  const isStartHigher =
    draft.start !== null && draft.end !== null && draft.start > draft.end;
  const validBounds = toValidBounds(draft);

  return (
    <DropdownContainer>
      <form
        onSubmit={event => {
          event.preventDefault();
          if (validBounds) onSubmit(validBounds);
        }}
      >
        <FlexBox
          padding="small"
          flexDirection="column"
          gap="small"
          minWidth="280px"
        >
          <NumberInput
            data-autofocus
            allowDecimals
            aria-label={labels.fromPrefix}
            value={draft.start ?? undefined}
            prefix={labels.fromPrefix}
            placeholder={labels.placeholder}
            max={draft.end ?? undefined}
            hasError={isStartHigher}
            aria-errormessage={getChildElementId(endInputId, 'error')}
            isReadOnly={isDisabled}
            onValueChange={value =>
              setDraft({ ...draft, start: toBound(value) })
            }
          />
          <NumberInput
            id={endInputId}
            allowDecimals
            aria-label={labels.toPrefix}
            value={draft.end ?? undefined}
            prefix={labels.toPrefix}
            placeholder={labels.placeholder}
            min={draft.start ?? undefined}
            hasError={isStartHigher}
            errorMessage={isStartHigher ? labels.startHigherError : undefined}
            isReadOnly={isDisabled}
            onValueChange={value => setDraft({ ...draft, end: toBound(value) })}
          />
          <TertiaryButton
            isFlexible={false}
            type="submit"
            isDisabled={isDisabled || validBounds === null}
          >
            {submitLabel}
          </TertiaryButton>
        </FlexBox>
      </form>
    </DropdownContainer>
  );
};

export type DateRangeLabels = {
  startLabel: string;
  endLabel: string;
  startHigherError: string;
};

export type DateRangePopoverProps = {
  type: 'dateRange';
  start: string | undefined;
  end: string | undefined;
  isStartAfterEnd: boolean;
  mode: RelativeDatePickerMode;
  placeholder: string;
  i18nLocale: I18nLocale;
  isDisabled?: boolean;
  labels: DateRangeLabels;
  setBounds: (bounds: {
    start: string | undefined;
    end: string | undefined;
  }) => void;
};

/**
 * Both ends of a date range, each accepting a relative date. Whether the
 * start is after the end is decided by the caller, which knows how to compare
 * two dates that may each be relative.
 */
export const DateRangePopover = ({
  start,
  end,
  isStartAfterEnd,
  mode,
  placeholder,
  i18nLocale,
  isDisabled,
  labels,
  setBounds,
}: DateRangePopoverProps) => (
  <DropdownContainer dropdownMaxHeight={DropdownMaxHeight.AUTO}>
    <FlexBox
      padding="small"
      flexDirection="column"
      gap="small"
      minWidth="300px"
    >
      <RelativeDatePicker
        label={labels.startLabel}
        value={start}
        placeholder={placeholder}
        i18nLocale={i18nLocale}
        onChange={value => setBounds({ start: value, end })}
        relativeDatePickerMode={mode}
        isDisabled={isDisabled}
        hasError={isStartAfterEnd}
      />
      <RelativeDatePicker
        label={labels.endLabel}
        value={end}
        placeholder={placeholder}
        i18nLocale={i18nLocale}
        onChange={value => setBounds({ start, end: value })}
        relativeDatePickerMode={mode}
        isDisabled={isDisabled}
        hasError={isStartAfterEnd}
        errorMessage={isStartAfterEnd ? labels.startHigherError : undefined}
      />
    </FlexBox>
  </DropdownContainer>
);
